import {
  Category,
  CustomPricingConfig,
  RCS_DEFAULT_PRICE,
  SlabPricingConfig,
  WHATSAPP_PRICING,
} from '@heltarchat/shared';

import { AdminBusiness, MessagingVolumeWithCost } from './types';

export type CountrySummary = {
  isoCode: string;
  totalVolume: number;
  totalCost: number;
};

export type CategorySummary = {
  category: string;
  totalVolume: number;
  totalCost: number;
};

/**
 * Group messaging volumes by country for summary display
 */
export const groupVolumesByCountry = (
  volumes: MessagingVolumeWithCost[],
): CountrySummary[] => {
  const grouped = new Map<string, CountrySummary>();

  for (const row of volumes) {
    const key = row.isoCode;
    if (grouped.has(key)) {
      const existing = grouped.get(key)!;
      existing.totalVolume += row.volume;
      existing.totalCost += row.cost;
    } else {
      grouped.set(key, {
        isoCode: row.isoCode,
        totalVolume: row.volume,
        totalCost: row.cost,
      });
    }
  }

  return Array.from(grouped.values()).sort((a, b) => b.totalCost - a.totalCost);
};

/**
 * Group messaging volumes by category for summary display
 */
export const groupVolumesByCategory = (
  volumes: MessagingVolumeWithCost[],
): CategorySummary[] => {
  const grouped = new Map<string, CategorySummary>();

  for (const row of volumes) {
    const key = (row.category || 'Uncategorized').toUpperCase();
    if (grouped.has(key)) {
      const existing = grouped.get(key)!;
      existing.totalVolume += row.volume;
      existing.totalCost += row.cost;
    } else {
      grouped.set(key, {
        category: key.charAt(0) + key.slice(1).toLowerCase(),
        totalVolume: row.volume,
        totalCost: row.cost,
      });
    }
  }

  return Array.from(grouped.values()).sort((a, b) => b.totalCost - a.totalCost);
};

// ============================================
// Constants
// ============================================

export const TABLE_HEIGHT = 'calc(var(--app-height, 100vh) - 340px)';

// ============================================
// Date Utilities — moved to `utils/dateRange` for reuse outside admin.
// Re-exported here so existing relative imports keep working.
// ============================================

export {
  MONTHS,
  getYearOptions,
  getDefaultPreviousMonth,
  getMonthDateRange,
} from 'utils/dateRange';

// ============================================
// Formatting Utilities
// ============================================

export const formatDate = (value: string | Date) =>
  new Date(value).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export const formatCurrency = (
  amount: number,
  currency: string = '₹',
): string => {
  return `${currency}${amount.toFixed(2)}`;
};

export const formatBizWhatsapp = (business: AdminBusiness) => {
  if (!business.bizWhatsappNumber) return '—';
  return business.countryCode
    ? `+${business.countryCode} ${business.bizWhatsappNumber}`
    : business.bizWhatsappNumber;
};

// ============================================
// Pricing Utilities
// ============================================

export const DEFAULT_INDIA_PRICING = WHATSAPP_PRICING['IN'];

export const formatPrice = (
  customPrice: number | null | undefined,
  defaultPrice: number,
): string => {
  if (customPrice !== null && customPrice !== undefined) {
    return `${customPrice.toFixed(4)}`;
  }
  return `${defaultPrice.toFixed(4)} (default)`;
};

/** Extract a category price from a customPricing JSONB object */
export const getCountryPrice = (
  customPricing: CustomPricingConfig | null | undefined,
  isoCode: string,
  category: Category,
): number | undefined => {
  return customPricing?.[isoCode]?.[category];
};

/* Org pricing is slab-based: a single base tier is a flat price (show the
   rate), more than one tier is a ladder (show "Tiered (N)"), none → default.
   Shared by the admin org table and the partner-dashboard orgs table. */
export const formatOrgCategoryPrice = (
  slabPricing: SlabPricingConfig | null | undefined,
  category: Category,
  defaultPrice: number,
): string => {
  const tiers = slabPricing?.IN?.[category];
  if (tiers && tiers.length > 1) {
    return `Tiered (${tiers.length})`;
  }
  const baseRate = tiers?.find(t => t.minVolume === 0)?.rate;
  return formatPrice(baseRate, defaultPrice);
};

/** RCS analog — categoryless, IN-only, defaulting to RCS_DEFAULT_PRICE. */
export const formatOrgRcsPrice = (
  slabPricing: SlabPricingConfig | null | undefined,
): string => {
  const tiers = slabPricing?.rcs?.IN;
  if (tiers && tiers.length > 1) {
    return `Tiered (${tiers.length})`;
  }
  const baseRate = tiers?.find(t => t.minVolume === 0)?.rate;
  return formatPrice(baseRate, RCS_DEFAULT_PRICE);
};
